import {Mapper} from '@s-ui/domain'

export class FromArticleDataToStructuredDataVOSeoMapper extends Mapper {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Object} deps.config
   * @param {Function} deps.structuredDataValueObjectFactory
   * */
  constructor({config, structuredDataValueObjectFactory}) {
    super()
    this._config = config
    this._companyUrl = config.get('COMPANY_URL')
    this._companyName = config.get('COMPANY_NAME')
    this._appName = this._config.get('APP_NAME')
    this._appNameCoches = this._config.get('APP_NAME_COCHES')
    this._urlStatics = config.get('urlStatics')
    this._hostNameDomain = config.get('HOST_NAME_DOMAIN')
    this._structuredDataValueObjectFactory = structuredDataValueObjectFactory
  }

  _getOrganizationId = () => {
    return `${this._companyUrl}/#organization`
  }

  _getWebSiteId = () => {
    return `${this._companyUrl}/#website`
  }

  _getAuthorId = ({author}) => {
    return `${this._companyUrl}/#author/${author.slug}`
  }

  _getVideoId = ({canonical}) => {
    return `${canonical}#video`
  }

  _getSocialProfileSameAs = () => {
    const {social: socialLinks} = this._config.get('links')

    const socialProfiles = socialLinks.map(link => link.url)

    return socialProfiles
  }

  _getOrganizationNode = () => {
    const appName = this._config.get('APP_NAME').toLowerCase()
    const urlStaticImages = this._config.get('URL_STATICS_IMAGES')

    const isCoches = this._appName === this._appNameCoches

    const logoCochesDimensions = {width: 724, height: 107}
    const logoMotosDimensions = {width: 724, height: 114}
    const logoDimensions = isCoches ? logoCochesDimensions : logoMotosDimensions

    return {
      '@type': 'Organization',
      '@id': this._getOrganizationId(),
      name: this._companyName,
      url: this._companyUrl,
      logo: {
        '@type': 'ImageObject',
        '@id': `${this._companyUrl}/#logo`,
        url: `${urlStaticImages}${appName}/logos/logo-${appName}@2x.png`,
        ...logoDimensions
      },
      sameAs: this._getSocialProfileSameAs()
    }
  }

  _getWebSiteNode = () => {
    return {
      '@type': 'WebSite',
      '@id': this._getWebSiteId(),
      name: this._companyName,
      url: this._companyUrl,
      publisher: {'@id': this._getOrganizationId()}
    }
  }

  _getPersonNode = ({author}) => {
    return {
      '@type': 'Person',
      '@id': this._getAuthorId({author}),
      name: author.name,
      image: {
        '@type': 'ImageObject',
        url: author.image?.url?.startsWith('//') ? `https:${author.image.url}` : author.image?.url
      },
      jobTitle: `Editor ${this._companyName}`,
      worksFor: {'@id': this._getOrganizationId()}
    }
  }

  _getWebPageNode = ({articleTitle, canonical}) => {
    return {
      '@type': 'WebPage',
      '@id': `${canonical}#webpage`,
      url: canonical,
      name: articleTitle,
      isPartOf: {'@id': this._getWebSiteId()},
      breadcrumb: {'@id': `${canonical}#breadcrumb`}
    }
  }

  _getBreadcrumbNode = ({breadcrumb, canonical}) => {
    const homeItem = {
      '@type': 'ListItem',
      position: 1,
      name: 'Inicio',
      item: `${this._companyUrl}/`
    }

    return {
      '@type': 'BreadcrumbList',
      '@id': `${canonical}#breadcrumb`,
      itemListElement: [
        homeItem,
        ...breadcrumb.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 2,
          name: item.label,
          item: item.url
        }))
      ]
    }
  }

  _getNewsArticleNode = ({
    articleTitle,
    author,
    canonical,
    category,
    culture,
    hasVideo,
    imageUrls,
    metaDescription,
    publicationDate,
    updateDate
  }) => {
    return {
      '@type': 'NewsArticle',
      '@id': `${canonical}#article`,
      headline: articleTitle,
      description: metaDescription,
      datePublished: publicationDate,
      dateModified: updateDate,
      ...(imageUrls.url16x9 && {
        image: [imageUrls.url16x9, imageUrls.url4x3, imageUrls.url1x1].filter(Boolean)
      }),
      inLanguage: culture,
      articleSection: category,
      isAccessibleForFree: true,
      mainEntityOfPage: {'@id': `${canonical}#webpage`},
      author: {'@id': this._getAuthorId({author})},
      publisher: {'@id': this._getOrganizationId()},
      ...(hasVideo && {
        video: {
          '@id': this._getVideoId({canonical})
        }
      })
    }
  }

  _getVideoObjectNode = ({canonical, publicationDate, video}) => {
    return {
      '@type': 'VideoObject',
      '@id': this._getVideoId({canonical}),
      name: video.title,
      embedUrl: video.embedUrl,
      thumbnailUrl: video.thumbnailHQ,
      uploadDate: publicationDate
    }
  }

  _getReviewNode = ({author, canonical, imageUrls, publicationDate, valoration, vehicles}) => {
    const [vehicle = {}] = vehicles

    const {make = {}, model = {}, year} = vehicle

    const makeName = make.name
    const modelName = model.name

    const name = `${makeName} ${modelName}${year ? ` ${year}` : ''}`

    return {
      '@type': ['Product', 'Car'],
      '@id': `${canonical}#product`,
      name,
      brand: {
        '@type': 'Brand',
        name: makeName
      },
      model: modelName,
      image: imageUrls.url16x9,
      review: {
        '@type': 'Review',
        author: {'@id': this._getAuthorId({author})},
        datePublished: publicationDate,
        reviewRating: {
          '@type': 'Rating',
          ratingValue: valoration?.globalScore?.value,
          bestRating: 10,
          worstRating: 1
        }
      }
    }
  }

  _getFaqsNode = ({canonical, faqs}) => {
    return {
      '@type': 'FAQPage',
      '@id': `${canonical}#faq`,
      mainEntity: faqs?.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    }
  }

  /**
   * https://schema.org/Vehicle
   * @return {StructuredDataValueObject}
   */
  map = articleData => {
    const {
      articleTitle,
      author = {},
      breadcrumb = [],
      canonical,
      category,
      culture,
      faqs = [],
      imageUrls = {},
      metaDescription,
      publicationDate,
      rating,
      updateDate,
      valoration,
      vehicles = [],
      video
    } = articleData

    const hasVideo = Boolean(video)

    const organizationNode = this._getOrganizationNode()
    const webSiteNode = this._getWebSiteNode()
    const personNode = this._getPersonNode({author})
    const webPageNode = this._getWebPageNode({articleTitle, canonical})
    const breadcrumbNode = this._getBreadcrumbNode({breadcrumb, canonical})
    const newsArticleNode = this._getNewsArticleNode({
      articleTitle,
      author,
      canonical,
      category,
      culture,
      hasVideo,
      imageUrls,
      metaDescription,
      publicationDate,
      updateDate
    })

    const baseNodes = [organizationNode, webSiteNode, personNode, webPageNode, breadcrumbNode, newsArticleNode]
    const optionalNodes = []

    if (hasVideo) {
      const videoObjectNode = this._getVideoObjectNode({canonical, publicationDate, video})
      optionalNodes.push(videoObjectNode)
    }

    const isOneVehicleArticle = Boolean(vehicles && vehicles.length === 1)
    const isOneVehicleValoration = Boolean(isOneVehicleArticle && valoration && valoration.length === 1)
    const shouldAddAggregateRating = Boolean(rating && rating.rate && rating.ratesCount)
    const hasReview = shouldAddAggregateRating && isOneVehicleValoration

    if (hasReview) {
      const reviewNode = this._getReviewNode({author, canonical, imageUrls, publicationDate, valoration, vehicles})
      optionalNodes.push(reviewNode)
    }

    const hasFaqs = Boolean(faqs && faqs.length)

    if (hasFaqs) {
      const faqsNode = this._getFaqsNode({canonical, faqs})
      optionalNodes.push(faqsNode)
    }

    const graph = [...baseNodes, ...optionalNodes]

    return this._structuredDataValueObjectFactory({
      '@context': 'https://schema.org',
      '@graph': graph
    })
  }
}
